/**
 * McpServerPage.tsx — MCP Server configuration page (per-org).
 *
 * The MCP server is a platform-level service (always running). This page
 * lets the org admin:
 * - Enable/disable MCP access for their org
 * - View connection instructions for AI clients
 * - Manage their org-scoped API key
 * - See the available tools
 *
 * @since 2026-06-20
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import AdminLayout from '../components/AdminLayout';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useOrgContext } from '../contexts/OrgContext';

interface McpConfig {
  enabled: boolean;
  endpoint: string;
  version: string;
  tools: { name: string; description: string }[];
  apiKey: string;
}

export default function McpServerPage(): React.JSX.Element {
  const { token, apiBaseUrl } = useAdminAuth();
  const { activeOrg } = useOrgContext();
  const apiBase = `${apiBaseUrl}/api/v1/orgs/${activeOrg?.orgId}`;
  const orgId = activeOrg?.orgId ?? '';
  const queryClient = useQueryClient();
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['mcp-config', orgId],
    queryFn: async () => {
      const res = await fetch(`${apiBase}/mcp/status`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to fetch MCP config');
      return res.json() as Promise<{ status: McpConfig }>;
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async (enable: boolean) => {
      const res = await fetch(`${apiBase}/mcp/${enable ? 'enable' : 'disable'}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to update MCP access');
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mcp-config'] }),
  });

  const regenerateKeyMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${apiBase}/mcp/regenerate-key`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to regenerate key');
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mcp-config'] }),
  });

  const config = data?.status;
  const baseUrl = apiBase.replace(/\/api\/v1\/orgs\/[^/]+$/, '');
  const mcpEndpoint = config?.endpoint || `${baseUrl}/mcp/${orgId}`;

  function copy(text: string, label: string) {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  }

  return (
    <AdminLayout title="MCP Server">
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px' }}>
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>MCP Server</h1>
          <p style={{ color: '#64748B', fontSize: '0.9rem', marginTop: 4 }}>
            The Model Context Protocol (MCP) server lets AI agents interact with your billing system programmatically.
            Agents in VS Code Copilot, Claude Desktop, Cursor, and other MCP-compatible clients can manage customers,
            subscriptions, invoices, and more — scoped to your organization via an API key.
          </p>
        </div>

        {isLoading && <p style={{ color: '#94A3B8' }}>Loading…</p>}

        {config && (
          <>
            {/* Platform Status + Enable/Disable */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
              <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 12, padding: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#16A34A' }} />
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#166534' }}>Platform MCP Server: Running</span>
                </div>
                <p style={{ fontSize: '0.8rem', color: '#15803D' }}>
                  The MCP server runs at the platform level and is always available. All authenticated requests
                  are scoped to the organization identified by the API key.
                </p>
                <p style={{ fontSize: '0.75rem', color: '#64748B', marginTop: 8 }}>Version: {config.version}</p>
              </div>

              <div style={{ background: config.enabled ? '#F0FDFA' : '#FFF7ED', border: `1px solid ${config.enabled ? '#99F6E4' : '#FED7AA'}`, borderRadius: 12, padding: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: config.enabled ? '#0F766E' : '#9A3412' }}>
                    MCP Access: {config.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                  <button
                    onClick={() => toggleMutation.mutate(!config.enabled)}
                    disabled={toggleMutation.isPending}
                    style={{
                      background: config.enabled ? '#FEE2E2' : '#0F766E',
                      color: config.enabled ? '#991B1B' : '#fff',
                      border: 'none', borderRadius: 6, padding: '6px 14px',
                      fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
                    }}
                  >
                    {config.enabled ? 'Disable' : 'Enable'}
                  </button>
                </div>
                <p style={{ fontSize: '0.8rem', color: '#64748B' }}>
                  {config.enabled
                    ? 'AI agents can access billing tools for this organization using the API key below.'
                    : 'MCP access is disabled. AI agents will be rejected when using this org\'s API key.'}
                </p>
              </div>
            </div>

            {/* How It Works */}
            <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 24, marginBottom: 24 }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 12 }}>How It Works</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                <div style={{ textAlign: 'center', padding: 16 }}>
                  <div style={{ fontSize: '2rem', marginBottom: 8 }}>🔑</div>
                  <h4 style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>1. Get Your API Key</h4>
                  <p style={{ fontSize: '0.75rem', color: '#64748B' }}>
                    Each org has a unique API key. The key identifies which organization the AI agent is acting on behalf of.
                  </p>
                </div>
                <div style={{ textAlign: 'center', padding: 16 }}>
                  <div style={{ fontSize: '2rem', marginBottom: 8 }}>⚙️</div>
                  <h4 style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>2. Configure Your Client</h4>
                  <p style={{ fontSize: '0.75rem', color: '#64748B' }}>
                    Add the MCP server to your AI client's config (VS Code, Claude Desktop, Cursor, etc.) with your API key.
                  </p>
                </div>
                <div style={{ textAlign: 'center', padding: 16 }}>
                  <div style={{ fontSize: '2rem', marginBottom: 8 }}>🤖</div>
                  <h4 style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>3. Ask Your Agent</h4>
                  <p style={{ fontSize: '0.75rem', color: '#64748B' }}>
                    Your AI agent can now manage customers, subscriptions, invoices, usage events, and more — all scoped to your org.
                  </p>
                </div>
              </div>
            </div>

            {/* API Key */}
            <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 20, marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div>
                  <h3 style={{ fontSize: '0.9rem', fontWeight: 600, margin: 0 }}>Organization API Key</h3>
                  <p style={{ fontSize: '0.75rem', color: '#64748B', marginTop: 2 }}>
                    This key authenticates AI agents to act on behalf of your organization. Keep it secret.
                  </p>
                </div>
                <button
                  onClick={() => { if (confirm('Regenerate the API key? Existing agent connections will need to be reconfigured.')) regenerateKeyMutation.mutate(); }}
                  style={{ fontSize: '0.8rem', color: '#DC2626', background: 'none', border: '1px solid #FCA5A5', borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}
                >
                  Regenerate Key
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <code style={{ flex: 1, background: '#F1F5F9', padding: '10px 14px', borderRadius: 8, fontSize: '0.8rem', fontFamily: 'monospace' }}>
                  {showKey ? (config.apiKey || 'not-set') : '••••••••••••••••••••••••••••••••••••••••••••••••'}
                </code>
                <button onClick={() => setShowKey(!showKey)} style={btnStyle}>
                  {showKey ? '🙈' : '👁️'}
                </button>
                <button onClick={() => copy(config.apiKey ?? '', 'key')} style={btnStyle}>
                  {copied === 'key' ? '✓ Copied' : '📋'}
                </button>
              </div>
            </div>

            {/* Connection Details */}
            <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 20, marginBottom: 24 }}>
              <h3 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 16 }}>Connection Details</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 4 }}>HTTP/SSE Endpoint</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <code style={{ flex: 1, background: '#F1F5F9', padding: '8px 12px', borderRadius: 6, fontSize: '0.75rem', wordBreak: 'break-all' }}>
                      {mcpEndpoint}
                    </code>
                    <button onClick={() => copy(mcpEndpoint, 'endpoint')} style={btnStyle}>
                      {copied === 'endpoint' ? '✓' : '📋'}
                    </button>
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 4 }}>Stdio Command (npx)</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <code style={{ flex: 1, background: '#F1F5F9', padding: '8px 12px', borderRadius: 6, fontSize: '0.75rem' }}>
                      npx @rule42/billing-mcp
                    </code>
                    <button onClick={() => copy('npx @rule42/billing-mcp', 'npx')} style={btnStyle}>
                      {copied === 'npx' ? '✓' : '📋'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Config examples */}
              <h4 style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 12 }}>Client Configuration</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 6 }}>VS Code / Copilot (settings.json)</label>
                  <pre style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: 12, fontSize: '0.7rem', overflow: 'auto', whiteSpace: 'pre', margin: 0 }}>
{JSON.stringify({
  "mcp": {
    "servers": {
      "rule42-billing": {
        "command": "npx",
        "args": ["@rule42/billing-mcp"],
        "env": {
          "BILLING_API_URL": baseUrl,
          "BILLING_API_KEY": "<your-api-key>"
        }
      }
    }
  }
}, null, 2)}
                  </pre>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 6 }}>Claude Desktop (config.json)</label>
                  <pre style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: 12, fontSize: '0.7rem', overflow: 'auto', whiteSpace: 'pre', margin: 0 }}>
{JSON.stringify({
  "mcpServers": {
    "rule42-billing": {
      "command": "npx",
      "args": ["@rule42/billing-mcp"],
      "env": {
        "BILLING_API_URL": baseUrl,
        "BILLING_API_KEY": "<your-api-key>"
      }
    }
  }
}, null, 2)}
                  </pre>
                </div>
              </div>
            </div>

            {/* Available Tools */}
            <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 20, marginBottom: 24 }}>
              <h3 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 4 }}>
                Available Tools ({config.tools?.length ?? 0})
              </h3>
              <p style={{ fontSize: '0.8rem', color: '#64748B', marginBottom: 16 }}>
                These tools are exposed to AI agents when connected. The agent's access is scoped to your org's data only.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                {(config.tools ?? []).map(tool => (
                  <div key={tool.name} style={{ padding: '10px 12px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.75rem', fontFamily: 'monospace', color: '#0F766E' }}>{tool.name}</div>
                    <div style={{ fontSize: '0.7rem', color: '#64748B', marginTop: 2 }}>{tool.description}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Usage Examples */}
            <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 20 }}>
              <h3 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 12 }}>What You Can Ask Your AI Agent</h3>
              <p style={{ fontSize: '0.8rem', color: '#64748B', marginBottom: 16 }}>
                Once connected, you can ask natural language questions and the agent will use the billing tools automatically:
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[
                  '"Show me all customers who signed up this month"',
                  '"Create a new customer for Acme Corp with email billing@acme.com"',
                  '"What plans do we have available?"',
                  '"Subscribe customer X to the Pro plan with a 14-day trial"',
                  '"Cancel the subscription for customer Y at period end"',
                  '"How many credits does customer Z have remaining?"',
                  '"Record 500 API calls of usage for customer X"',
                  '"Check if customer Y has access to the analytics feature"',
                  '"Show me all unpaid invoices"',
                  '"What does our pricing page look like?"',
                ].map((example, i) => (
                  <div key={i} style={{ padding: '8px 12px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: '0.8rem', color: '#334155', fontStyle: 'italic' }}>
                    {example}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

const btnStyle: React.CSSProperties = {
  fontSize: '0.8rem', cursor: 'pointer', border: '1px solid #CBD5E1',
  borderRadius: 6, padding: '6px 10px', background: '#F8FAFC',
};
